import { useMemo } from "react";

import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";
import { useTranslation } from "react-i18next";
import { adaptRight } from "@/utils/adapter";
const useStyle = () => {
  const { theme, neutralColor, primaryColor, secondColor } = useGomakeTheme();
  const { t } = useTranslation();
  const direction = t("direction");
  const classes = useMemo(() => {
    return {
      detailTitle: {
        ...FONT_FAMILY.Lexend(500, 14),
        color: neutralColor(500),
        whiteSpace: "nowrap" as const,
      },
      insideStyle: {
        // width: "600px",
        height: "300px",
      },
      detailValue: {
        ...FONT_FAMILY.Inter(500, 14),
        color: primaryColor(900),
        whiteSpace: "nowrap" as const,
      },
      textAreaDetailValue: {
        ...FONT_FAMILY.Inter(900, 16),
        color: primaryColor(900),
      },
      sectionTitle: {
        color: primaryColor(900),
        ...FONT_FAMILY.Lexend(500, 14),
      },
      mobileSectionTitle: {
        color: primaryColor(900),
        ...FONT_FAMILY.Lexend(500, 13),
      },
      actionContainer: {
        backgroundColor: "#FFF",
        padding: "0 16px",
        borderRadius: 16,
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box" as const,
      },
      actionDragRow: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        minWidth: 0,
      },
      actionDragRowCompact: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        width: "100%",
        minWidth: 0,
      },
      actionHeaderRow: {
        padding: "10px 0",
        flexDirection: "row" as const,
        justifyContent: "space-between",
        alignItems: "center",
        gap: 0,
      },
      actionHeaderRowCompact: {
        padding: "10px 0",
        flexDirection: "column" as const,
        justifyContent: "flex-start",
        alignItems: "stretch",
        gap: "8px",
      },
      actionContentRow: {
        flexDirection: "row" as const,
        gap: "8px",
        alignItems: "center",
        flexWrap: "wrap" as const,
        minWidth: 0,
        flex: 1,
      },
      actionContentRowCompact: {
        flexDirection: "row" as const,
        gap: "10px 12px",
        alignItems: "center",
        flexWrap: "wrap" as const,
        rowGap: "10px",
        minWidth: 0,
        width: "100%",
      },
      actionIconsRow: {
        display: "flex",
        flexDirection: "row" as const,
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
      },
      actionIconsRowCompact: {
        display: "flex",
        flexDirection: "row" as const,
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
        alignSelf: "flex-end",
        width: "100%",
      },
      actionContainerBorder: "2px solid " + primaryColor(500),
      toggleActionButton: {
        backgroundColor: primaryColor(50),
        borderRadius: 8,
        height: 40,
        width: 40,
      },
      toggleActionButtonMobile: {
        backgroundColor: primaryColor(50),
        borderRadius: 8,
        height: 30,
        width: 30,
      },
      toggleSubWorkFlowActionButton: {
        backgroundColor: primaryColor(600),
        borderRadius: 8,
        height: 30,
        width: 30,
        color: "white",
      },
      workFlowContainer: {
        padding: "10px 16px",
        borderRadius: 16,
        backgroundColor: "#F9FAFB",
      },
      subWorkFlowContainer: {
        padding: "10px 16px",
        borderRadius: "8px",
        backgroundColor: primaryColor(300),
        color: "white",
      },
      buttonGroup: {
        borderRadius: "0px",
        overflow: "hidden",
        display: "flex",
        flexWrap: "nowrap",
        width: "max-content",
        position: "relative",
      },
      tabsContainerSx: {
        "&::-webkit-scrollbar": {
          height: "10px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#CBCDCF",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#667085",
        },
      },
      tabsContainer: {
        overflowX: "auto" as const,
        overflowY: "hidden" as const,
      },
      fixedOutSourceButton: {
        width: "fit-content",
        minWidth: "fit-content",
        border: "none",
        borderRadius: 0,
        "&:hover": {
          border: `2px solid ${primaryColor(500)}`,
        },
      },
      button: {
        minWidth: "fit-content",
        width: "fit-content",
        borderRadius: 0,
        border: 0,
        flex: "1 1 auto",
        whiteSpace: "nowrap",
        letterSpacing: "0em",
        transition: "0s",
        position: "relative",
        "&:hover": {
          minWidth: "fit-content",
          flex: "1 1 auto",
          borderRadius: 0,
          letterSpacing: "0em",
          transition: "0s",
          border: `2px solid ${primaryColor(500)}`,
        },
      },
      sourceLabel: {
        padding: "3px 10px",
        backgroundColor: "#F4F3FF",
        ...FONT_FAMILY.Lexend(500, 14),
        color: "#5925DC",
        borderRadius: "16px",
        textAlign: "center" as "center",
      },
      subWorkFlowDividerVertical: {
        width: "2px",
        backgroundColor: "#667085",
        margin: "",
        top: 0,
        bottom: "50%",
        height: "50%",
        ...adaptRight(direction, 5),
      },
      nameStyle: {
        // marginRight: 12,
        ...FONT_FAMILY.Inter(400, 12),
        color: "#FFF",
      },
      secondPartStyle: {
        ...FONT_FAMILY.Inter(700, 14),
        color: "#FFF",
      },
      totalRealProductionTimeStyle: {
        display: "flex",
        flexDirection: "row" as "row",
        alignItems: "center",
        gap: "10px",
      },
      lineStyle: {
        ...FONT_FAMILY.Inter(500, 14),
        color: "#FFF",
        height: 20,
        width: 1,
        backgroundColor: "#FFF",
      },
      modalStyle: {
        width: "45%",
        minWidth: "350px",
        borderRadius: "8px",
        height: "auto",
        gap: "10px",
        zIndex: 5,
        paddingTop: 0,
      },
      autoCompleteSelectedAction: {
        width: 200,
        height: 36,
        border: "1px solid #D0D5DD",
        borderRadius: 8,
        boxShadow: "none",
        backgroundColor: "#FFF",
      },
      autoCompleteSelectedSectionName: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        width: 140,
        height: 36,
        border: "1px solid #D0D5DD",
        borderRadius: 8,
        boxShadow: "none",
        backgroundColor: "#FFF",
        color: "#344054",
        ...FONT_FAMILY.Inter(500, 14),
      },
      iconColor: `var(--theme-icon-color, ${primaryColor(500)})`,
    };
  }, [theme, direction]);
  return {
    classes,
    primaryColor,
    secondColor,
  };
};
export { useStyle };
